import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import Editor, {
  TABLE_CELL_COMPONENT,
  NOT_APPLICABLE,
  FORM_FIELD_COMPONENT,
} from './Editor';

const makeMultilineKey = (line, i) => `${i}`;

const makeComponents = (formatter, options) => {
  class FormatBasedComponent extends React.PureComponent {
    static propTypes = {
      onChange: PropTypes.func.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.bool,
        PropTypes.shape({}),
        PropTypes.arrayOf(PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
          PropTypes.bool,
          PropTypes.shape({}),
        ])),
      ]),
      editing: PropTypes.bool,
      label: PropTypes.string,
      type: PropTypes.string,
      error: PropTypes.string,
      readOnly: PropTypes.bool,
      form: PropTypes.bool,
      object: PropTypes.shape({}),
      property: PropTypes.shape({
        formatValue: PropTypes.func,
        name: PropTypes.string,
        type: PropTypes.string,
        margin: PropTypes.string,
        multiline: PropTypes.bool,
        description: PropTypes.string,
        debounce: PropTypes.number,
        align: PropTypes.string,
      }),
    }

    static defaultProps = {
      value: undefined,
      error: null,
      readOnly: false,
      label: null,
      editing: false,
      form: false,
      property: undefined,
      type: undefined,
      object: undefined,
    };

    constructor(props) {
      super(props);

      this.handleChange = this.handleChange.bind(this);
    }

    getType() {
      const { property = {}, type } = this.props;
      return type || property.type || options.type || 'number';
    }

    handleChange = (event) => {
      const { onChange } = this.props;
      const type = this.getType();
      if (onChange) {
        let { value } = event.target;
        if (type === 'integer') {
          value = parseInt(value, 10);
        } else if (type === 'number') {
          value = parseFloat(value);
        }
        onChange(value);
      }
    };

    render() {
      const {
        value,
        editing,
        error,
        label,
        readOnly,
        form,
        property = {},
        object,
      } = this.props;
      let displayValue = !editing && formatter(value, object, this.props);
      const type = this.getType();
      const textAlign = property.align || options.align || (!type || (type === 'number') ? 'right' : undefined);

      const InputProps = {
        readOnly: Boolean(readOnly === undefined ? true : readOnly),
        startAdornment: options.startAdornment ? <InputAdornment position="start">{options.startAdornment}</InputAdornment> : undefined,
        endAdornment: options.endAdornment ? <InputAdornment position="end">{options.endAdornment}</InputAdornment> : undefined,
        inputProps: textAlign ? { style: { textAlign } } : undefined,
        fullWidth: Boolean(options.fullWidth),
      };
      if (options && options.multiline && Array.isArray(displayValue)) {
        if (editing || form) {
          displayValue = displayValue.join(options.multilineSeparator || ' - ');
        } else {
          let values = displayValue || [];
          let cut = false;
          if (options.multilineMax && (values.length > options.multilineMax)) {
            values = values.slice(0, options.multilineMax);
            cut = true;
          }
          return (
            <div className={classnames('tuc-textfield-editor', 'tuc-textfield-multine-editor', textAlign === 'right' && 'tuc-textfield-multine-editor-right', textAlign === 'center' && 'tuc-textfield-multine-editor-center', cut && 'tuc-textfield-multine-editor-cut')}>
              {values.map((line, i) => (
                <Typography variant="body2" key={makeMultilineKey(line, i)}>{line}</Typography>
              ))}
              {cut && (
                <Typography variant="body2" key="multine-ellipsis" className="tuc-textfield-multine-editor-ellipsis">...</Typography>
              )}
            </div>
          );
        }
      }

      return (
        <div className={classnames('tuc-textfield-editor', InputProps.readOnly && 'grbrsm-readonly', form && options.formClassName || options.className)}>
          {(editing || form) ? (
            <TextField
              error={!!error}
              label={error || label}
              value={readOnly ? displayValue : (value || 0)}
              type={readOnly ? 'string' : type}
              margin={form ? 'normal' : 'dense'}
              onChange={this.handleChange}
              InputProps={InputProps}
            />
          ) : (
            displayValue
          )}
        </div>
      );
    }
  }

  return {
    [TABLE_CELL_COMPONENT]: [options.tableComponent || FormatBasedComponent, options.tableComponent || FormatBasedComponent],
    [FORM_FIELD_COMPONENT]: [options.formComponent || FormatBasedComponent],
  };
};

export default class FormatBasedEditor extends Editor {
  constructor(name, formatter, options = {}) {
    super(name, makeComponents(formatter, options));
    this.formatter = formatter;
    this.options = options;
    if (this.options.value) {
      this.getValue = this.options.value;
    }
  }

  getFilterValue(value, fvalue, obj) {
    return this.options.filterValue ? this.options.filterValue(value, fvalue, obj) : fvalue;
  }

  getSortValue(value, fvalue, obj) {
    return this.options.sortValue ? this.options.sortValue(value, fvalue, obj) : value;
  }

  formatValue(value, object) {
    return value === undefined
      ? (this.options.undefinedText || NOT_APPLICABLE) : this.formatter(value, object);
  }
}
